import React from "react";
import { useNavigate } from "react-router";
import {
    Archive20Regular,
    ArrowClockwise20Regular,
    ArrowDownload20Regular,
    ArrowUndo20Regular,
    Attach16Regular,
    ChevronDown16Regular,
    ChevronLeft20Regular,
    ChevronRight20Regular,
    Comment16Regular,
    Dismiss16Regular,
    Filter20Regular,
    DocumentPdf20Regular,
    FolderArrowRight20Regular,
    Open20Regular,
    TaskListAdd20Regular,
    TaskListSquareLtr20Regular,
} from "@fluentui/react-icons";
import { zeigeGlobalenHinweis } from "../calendar/globalerHinweis";
import { ACTIVE_BG, AUB, BG_SOFT, GOLD, INK, INK2, LINE } from "../chat/chatTheme";
import { MailSearchInput } from "../mail/MailSearchInput";
import { useResponsive } from "../../hooks/useResponsive";
import { useDocumentViewer, useFileViewer } from "../documents/useDocumentViewer";
import { PdfFileViewer } from "../documents/PdfFileViewer";
import { Toast } from "../redesign/Toast";
import { DvagPostKontextMenue, type DvagPostMenue } from "./DvagPostKontextMenue";
import { useDvagPostAktionen, type DvagAbgelegtHinweis } from "./useDvagPostAktionen";
import {
    DVAG_BEREICH_TITEL,
    DVAG_POST_GEAENDERT,
    dvagArtZeile,
    dvagDatumsGruppe,
    dvagFilterAnzahl,
    dvagPostService,
    meldeDvagPostGeaendert,
    type DvagBereich,
    type DvagFilter,
    type DvagFilterWerte,
    type DvagOrdner,
    type DvagPostDetail,
    type DvagPostZeile,
} from "../../services/dvagPostService";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

/**
 * DVAG-Kontakthistorie im Schriftverkehr: links die Einträge wie Mails,
 * rechts der geöffnete Eintrag mit den Daten der DVAG und dem PDF.
 *
 * Eigene Ansicht statt eines Postfachs der Mail-Ansicht: die Einträge kommen
 * aus der KMVS-Datenbank, nicht aus Outlook.
 */

const ORDNER_TITEL: Record<DvagOrdner, string> = {
    posteingang: "Posteingang",
    archiv: "Archiv",
};

/** Felder, die die DVAG im Kopf des Eintrags zeigt; der Rest steht im Inhalt. */
const KOPF_FELDER = ["Gesendet", "Geändert", "Empfänger", "Absender"];

const knopf = (primaer = false, aus = false): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 6,
    border: primaer ? `1px solid ${AUB}` : `1px solid ${LINE}`,
    background: primaer ? AUB : "#fff",
    color: primaer ? "#fff" : INK,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    cursor: aus ? "default" : "pointer",
    opacity: aus ? 0.55 : 1,
});

const linkKnopf: React.CSSProperties = {
    border: "none",
    background: "transparent",
    padding: 0,
    color: AUB,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "inherit",
    textDecoration: "underline",
    cursor: "pointer",
};

const groesse = (bytes: number | null): string => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

/* ───────────────────────── Detail ───────────────────────── */

const DvagPostDetailAnsicht: React.FC<{
    zeile: DvagPostZeile;
    onGeaendert: (neu: DvagPostDetail) => void;
    /** T-298: Der Eintrag ist beim Kunden abgelegt und damit verschwunden. */
    onEntfernt: (id: number, hinweis: DvagAbgelegtHinweis) => void;
    onSchliessen?: () => void;
}> = ({ zeile, onGeaendert, onEntfernt, onSchliessen }) => {
    const navigate = useNavigate();
    const [eintrag, setEintrag] = React.useState<DvagPostDetail | null>(null);
    const [fehler, setFehler] = React.useState<string | null>(null);
    const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
    const [pdfFehler, setPdfFehler] = React.useState(false);
    // `onGeaendert` wechselt mit jedem Render der Liste die Identität; als Ref
    // gehalten, damit das Laden nur an der Eintrags-ID hängt.
    const onGeaendertRef = React.useRef(onGeaendert);
    onGeaendertRef.current = onGeaendert;

    React.useEffect(() => {
        const ab = new AbortController();
        setEintrag(null);
        setFehler(null);
        dvagPostService
            .detail(zeile.id, ab.signal)
            .then((d) => {
                setEintrag(d);
                onGeaendertRef.current(d);
            })
            .catch((err: unknown) => {
                if ((err as { name?: string })?.name !== "AbortError") {
                    setFehler("Der Eintrag konnte nicht geladen werden.");
                }
            });
        return () => ab.abort();
    }, [zeile.id]);

    React.useEffect(() => {
        setPdfUrl(null);
        setPdfFehler(false);
        if (!zeile.hatPdf) return;
        const ab = new AbortController();
        let url: string | null = null;
        dvagPostService
            .pdf(zeile.id, ab.signal)
            .then((blob) => {
                url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
                setPdfUrl(url);
            })
            .catch((err: unknown) => {
                if ((err as { name?: string })?.name !== "AbortError") setPdfFehler(true);
            });
        return () => {
            ab.abort();
            if (url) URL.revokeObjectURL(url);
        };
    }, [zeile.id, zeile.hatPdf]);

    // Die Zeile der Liste ist nach jeder Aktion der neueste Stand — auch nach einer
    // aus dem Kontextmenü (T-293), die dieses Detail nicht selbst ausgelöst hat.
    const aktuell: DvagPostZeile = eintrag ? { ...eintrag, ...zeile } : zeile;

    // T-293: dieselben Aktionen wie das Kontextmenü der Liste.
    const aktionen = useDvagPostAktionen({
        onGeaendert: (neu) => {
            setEintrag(neu);
            onGeaendert(neu);
        },
        onEntfernt,
        onFehler: setFehler,
    });
    const beschaeftigt = aktionen.beschaeftigt;
    const neuerVorgang = () => aktionen.neuerVorgang(aktuell);
    const bestehendemVorgangZuordnen = () => aktionen.bestehendemVorgangZuordnen(aktuell);
    const kontaktZuweisen = () => aktionen.kontaktZuweisen(aktuell);
    const beimKundenAblegen = () => aktionen.beimKundenAblegen(aktuell);

    const [vorgangMenue, setVorgangMenue] = React.useState(false);
    const vorgangMenueRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        if (!vorgangMenue) return;
        const zu = (e: MouseEvent | KeyboardEvent) => {
            if (e instanceof KeyboardEvent ? e.key === "Escape" : !vorgangMenueRef.current?.contains(e.target as Node)) {
                setVorgangMenue(false);
            }
        };
        alleDokumente.addEventListener("mousedown", zu);
        alleDokumente.addEventListener("keydown", zu);
        return () => {
            alleDokumente.removeEventListener("mousedown", zu);
            alleDokumente.removeEventListener("keydown", zu);
        };
    }, [vorgangMenue]);

    const oeffneDatei = useFileViewer();
    const oeffneDokument = useDocumentViewer();
    /**
     * Öffnet das PDF im KMVS-PDF-Fenster statt im Betrachter des Browsers. Liegt es
     * schon als KMVS-Dokument am Vorgang, öffnet sich dieses (mit Bearbeiten).
     */
    const pdfOeffnen = () => {
        const fileName = aktuell.pdfName || `${aktuell.betreff || "Dokument"}.pdf`;
        if (aktuell.documentId != null) {
            oeffneDokument({ id: aktuell.documentId, fileName, mimeType: "application/pdf" });
            return;
        }
        const id = aktuell.id;
        oeffneDatei({
            key: `dvag-post-${id}`,
            fileName,
            mimeType: "application/pdf",
            load: () => dvagPostService.pdf(id),
            onDownload: pdfHerunterladen,
        });
    };

    const pdfHerunterladen = () => {
        if (!pdfUrl) return;
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = aktuell.pdfName || `${aktuell.betreff || "dokument"}.pdf`;
        a.click();
    };

    const felder = eintrag?.detail?.felder ?? {};
    const kopfFelder = KOPF_FELDER.filter((k) => k in felder);
    const inhaltFelder = Object.keys(felder).filter((k) => !KOPF_FELDER.includes(k) && k !== "Priorität");
    const prioritaet = felder["Priorität"] || aktuell.prioritaet;
    const archiviert = aktuell.archiviertAm != null;

    const feldName: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: INK, whiteSpace: "nowrap" };
    const feldWert: React.CSSProperties = { fontSize: 13, color: INK, minWidth: 0, overflowWrap: "anywhere" };

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            {/* Kopf: dieselben vier Angaben wie in der Liste, dazu die Aktionen. */}
            <div style={{ padding: "14px 18px 12px", borderBottom: `1px solid ${LINE}`, background: BG_SOFT, flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    {onSchliessen && (
                        <button
                            type="button"
                            onClick={onSchliessen}
                            aria-label="Zurück zur Liste"
                            title="Zurück zur Liste"
                            style={{ ...knopf(), padding: 6 }}
                        >
                            <ChevronLeft20Regular />
                        </button>
                    )}
                    {/* Feste Mindestbreite: auf schmalen Bildschirmen rutschen die Knöpfe
                        darunter, statt den Namen auf eine Silbe je Zeile zu quetschen. */}
                    <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    color: AUB,
                                    background: "#f1e9f2",
                                    borderRadius: 999,
                                    padding: "2px 9px",
                                }}
                            >
                                {dvagArtZeile(aktuell)}
                            </span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: INK2 }}>
                                <Attach16Regular />
                                {aktuell.hatPdf ? "Dokument dabei" : "Ohne Dokument"}
                            </span>
                            {aktuell.hatKommentar && (
                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: INK2 }}>
                                    <Comment16Regular />
                                    Kommentar in der DVAG
                                </span>
                            )}
                            {prioritaet && prioritaet !== "Normal" && (
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#a4262c" }}>
                                    Priorität {prioritaet}
                                </span>
                            )}
                        </div>
                        <div style={{ fontSize: 17, fontWeight: 700, color: INK, marginTop: 6, overflowWrap: "anywhere" }}>
                            {aktuell.name || "Ohne Namen"}
                        </div>
                        <div style={{ fontSize: 14, color: INK, marginTop: 2, overflowWrap: "anywhere" }}>
                            {aktuell.betreff || "Kein Betreff"}
                        </div>
                        {aktuell.nummer && (
                            <div style={{ fontSize: 12, color: INK2, marginTop: 2 }}>Nr. {aktuell.nummer}</div>
                        )}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {aktuell.vorgangId ? (
                            <button
                                type="button"
                                style={knopf(true)}
                                onClick={() => aktionen.vorgangOeffnen(aktuell)}
                            >
                                <Open20Regular />
                                Vorgang öffnen
                            </button>
                        ) : (
                            <div ref={vorgangMenueRef} style={{ position: "relative" }}>
                                <button
                                    type="button"
                                    style={knopf(true, beschaeftigt)}
                                    disabled={beschaeftigt}
                                    aria-haspopup="menu"
                                    aria-expanded={vorgangMenue}
                                    onClick={() => setVorgangMenue((o) => !o)}
                                >
                                    <TaskListAdd20Regular />
                                    Vorgang
                                    <ChevronDown16Regular />
                                </button>
                                {vorgangMenue && (
                                    <div
                                        role="menu"
                                        style={{
                                            position: "absolute",
                                            top: "calc(100% + 4px)",
                                            left: 0,
                                            zIndex: 20,
                                            minWidth: 250,
                                            padding: 4,
                                            background: "#fff",
                                            border: `1px solid ${LINE}`,
                                            borderRadius: 8,
                                            boxShadow: "0 8px 24px -8px rgba(44,10,38,.35)",
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        {(
                                            [
                                                ["Neuer Vorgang", <TaskListAdd20Regular key="i" />, () => neuerVorgang()],
                                                ["Bestehendem Vorgang zuordnen", <TaskListSquareLtr20Regular key="i" />, bestehendemVorgangZuordnen],
                                            ] as Array<[string, React.ReactNode, () => void]>
                                        ).map(([titel, icon, aktion]) => (
                                            <button
                                                key={titel}
                                                type="button"
                                                role="menuitem"
                                                className="dvag-menue-punkt"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 8,
                                                    padding: "8px 10px",
                                                    border: "none",
                                                    borderRadius: 6,
                                                    background: "transparent",
                                                    color: INK,
                                                    fontSize: 13,
                                                    fontFamily: "inherit",
                                                    textAlign: "left",
                                                    whiteSpace: "nowrap",
                                                    cursor: "pointer",
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.background = ACTIVE_BG)}
                                                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                                onClick={() => {
                                                    setVorgangMenue(false);
                                                    aktion();
                                                }}
                                            >
                                                {icon}
                                                {titel}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {aktuell.hatPdf && (
                            <button
                                type="button"
                                style={knopf(false, beschaeftigt)}
                                disabled={beschaeftigt}
                                title="Das Dokument in einen Ordner der Kundenakte legen; danach verschwindet der Eintrag aus der Kontakthistorie."
                                onClick={beimKundenAblegen}
                            >
                                <FolderArrowRight20Regular />
                                Beim Kunden ablegen
                            </button>
                        )}
                        <button
                            type="button"
                            style={knopf(false, beschaeftigt)}
                            disabled={beschaeftigt}
                            onClick={() => void aktionen.archivUmschalten(aktuell)}
                        >
                            {archiviert ? <ArrowUndo20Regular /> : <Archive20Regular />}
                            {archiviert ? "In den Posteingang" : "Archivieren"}
                        </button>
                    </div>
                </div>

                {/* Zuordnung: bestätigter Kontakt oder Vorschlag aus Nummer und Name. */}
                <div style={{ marginTop: 10, fontSize: 13, color: INK, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    {aktuell.kontakt ? (
                        <>
                            <span style={{ color: INK2 }}>Kontakt:</span>
                            <button type="button" style={linkKnopf} onClick={() => navigate(`/contacts/${aktuell.kontakt!.id}`)}>
                                {aktuell.kontakt.name}
                            </button>
                            <button type="button" style={{ ...linkKnopf, fontWeight: 500 }} onClick={kontaktZuweisen}>
                                ändern
                            </button>
                        </>
                    ) : aktuell.vorschlag ? (
                        <>
                            <span style={{ color: INK2 }}>Vorschlag:</span>
                            <button type="button" style={linkKnopf} onClick={() => navigate(`/contacts/${aktuell.vorschlag!.id}`)}>
                                {aktuell.vorschlag.name}
                            </button>
                            {aktuell.vorschlag.grund && (
                                <span style={{ color: INK2 }}>(erkannt über {aktuell.vorschlag.grund})</span>
                            )}
                            <button
                                type="button"
                                style={knopf(false, beschaeftigt)}
                                disabled={beschaeftigt}
                                onClick={() => void aktionen.vorschlagUebernehmen(aktuell)}
                            >
                                Übernehmen
                            </button>
                            <button type="button" style={{ ...linkKnopf, fontWeight: 500 }} onClick={kontaktZuweisen}>
                                Anderen Kontakt zuweisen
                            </button>
                        </>
                    ) : (
                        <>
                            <span style={{ color: INK2 }}>Kein Kontakt erkannt.</span>
                            <button type="button" style={linkKnopf} onClick={kontaktZuweisen}>
                                Kontakt zuweisen
                            </button>
                        </>
                    )}
                </div>
                {fehler && (
                    <div role="alert" style={{ marginTop: 8, fontSize: 13, color: "#a4262c" }}>
                        {fehler}
                    </div>
                )}
            </div>

            {/* Inhalt: die Angaben, wie die DVAG sie im Eintrag zeigt. */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
                {kopfFelder.length > 0 && (
                    <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto 1fr", gap: "6px 14px", alignItems: "baseline" }}>
                        {kopfFelder.map((k) => (
                            <React.Fragment key={k}>
                                <span style={feldName}>{k}</span>
                                <span style={feldWert}>{felder[k] || "–"}</span>
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={feldName}>Anhang</span>
                    {aktuell.hatPdf ? (
                        <>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 13, color: INK }}>
                                <DocumentPdf20Regular style={{ color: "#c4314b" }} />
                                {aktuell.pdfName || "Dokument.pdf"}
                                {aktuell.pdfBytes ? <span style={{ color: INK2 }}>({groesse(aktuell.pdfBytes)})</span> : null}
                            </span>
                            <button
                                type="button"
                                style={knopf()}
                                onClick={pdfOeffnen}
                            >
                                <Open20Regular />
                                Öffnen
                            </button>
                            <button type="button" style={knopf(false, !pdfUrl)} disabled={!pdfUrl} onClick={pdfHerunterladen}>
                                <ArrowDownload20Regular />
                                Herunterladen
                            </button>
                            {/* T-292 (28.10.2026): statt „Herunterladen" und in der Akte
                                wieder hochladen — direkt in einen Ordner der Kundenakte. */}
                            <button
                                type="button"
                                style={knopf(false, beschaeftigt)}
                                disabled={beschaeftigt}
                                title="Das Dokument in einen Ordner der Kundenakte legen; danach verschwindet der Eintrag aus der Kontakthistorie."
                                onClick={beimKundenAblegen}
                            >
                                <FolderArrowRight20Regular />
                                In Kundenakte ablegen
                            </button>
                        </>
                    ) : (
                        <span style={{ fontSize: 13, color: INK2 }}>Kein Dokument</span>
                    )}
                </div>

                {inhaltFelder.length > 0 && (
                    <div
                        style={{
                            border: `1px solid ${LINE}`,
                            borderRadius: 8,
                            padding: "12px 14px",
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                            gap: "8px 24px",
                        }}
                    >
                        {inhaltFelder.map((k) => (
                            <div key={k} style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: 10, alignItems: "baseline" }}>
                                <span style={feldName}>{k}</span>
                                <span style={feldWert}>{felder[k] || "–"}</span>
                            </div>
                        ))}
                    </div>
                )}
                {!eintrag && !fehler && <div style={{ fontSize: 13, color: INK2 }}>Eintrag wird geladen …</div>}

                {aktuell.hatPdf && (
                    <div style={{ flex: 1, minHeight: 420, border: `1px solid ${LINE}`, borderRadius: 8, overflow: "hidden", display: "flex" }}>
                        {pdfUrl ? (
                            <div style={{ flex: 1, minWidth: 0, minHeight: 0, position: "relative" }}>
                                <div style={{ position: "absolute", inset: 0 }}>
                                    <PdfFileViewer url={pdfUrl} />
                                </div>
                            </div>
                        ) : (
                            <div style={{ margin: "auto", fontSize: 13, color: pdfFehler ? "#a4262c" : INK2, padding: 24, textAlign: "center" }}>
                                {pdfFehler ? "Das Dokument konnte nicht geladen werden." : "Dokument wird geladen …"}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

/* ───────────────────────── Liste + Rahmen ───────────────────────── */

/* ───────────────────────── Filter ───────────────────────── */

const feldStil: React.CSSProperties = {
    height: 30,
    padding: "0 8px",
    borderRadius: 6,
    border: `1px solid ${LINE}`,
    background: "#fff",
    color: INK,
    fontSize: 13,
    fontFamily: "inherit",
    minWidth: 0,
    maxWidth: "100%",
};

const FilterFeld: React.FC<{ titel: string; children: React.ReactNode }> = ({ titel, children }) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, fontWeight: 600, color: INK2, minWidth: 0 }}>
        {titel}
        {children}
    </label>
);

/** Auswahl mit „Alle" als leerem Wert; ändert genau einen Schlüssel des Filters. */
function Auswahl<K extends keyof DvagFilter>(props: {
    titel: string;
    feld: K;
    filter: DvagFilter;
    setze: (feld: K, wert: DvagFilter[K] | undefined) => void;
    optionen: Array<[string, string]>;
    alle?: string;
}) {
    const { titel, feld, filter, setze, optionen, alle = "Alle" } = props;
    return (
        <FilterFeld titel={titel}>
            <select
                value={(filter[feld] as string | undefined) ?? ""}
                onChange={(e) => setze(feld, (e.target.value || undefined) as DvagFilter[K] | undefined)}
                style={feldStil}
            >
                <option value="">{alle}</option>
                {optionen.map(([wert, text]) => (
                    <option key={wert} value={wert}>
                        {text}
                    </option>
                ))}
            </select>
        </FilterFeld>
    );
}

const DvagFilterLeiste: React.FC<{
    bereich: DvagBereich;
    filter: DvagFilter;
    werte: DvagFilterWerte | null;
    onChange: (f: DvagFilter) => void;
}> = ({ bereich, filter, werte, onChange }) => {
    const setze = <K extends keyof DvagFilter>(feld: K, wert: DvagFilter[K] | undefined) => {
        const neu = { ...filter };
        if (wert === undefined || wert === "" || wert === false) delete neu[feld];
        else neu[feld] = wert;
        onChange(neu);
    };
    const liste = (w: string[] | undefined): Array<[string, string]> => (w ?? []).map((x) => [x, x]);
    const teams: Array<[string, string]> = [
        ...(werte?.teams ?? []).map((t): [string, string] => [t.id, `${t.name} (${t.anzahl})`]),
        ...(werte?.ohneTeam ? [["ohne", `Ohne Team (${werte.ohneTeam})`] as [string, string]] : []),
    ];
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                gap: "8px 12px",
                padding: "10px 12px 12px",
                borderBottom: `1px solid ${LINE}`,
                background: BG_SOFT,
                flexShrink: 0,
            }}
        >
            {teams.length > 0 && (
                <Auswahl titel="Team" feld="team" filter={filter} setze={setze} optionen={teams} alle="Alle Teams" />
            )}
            {/* In der Schadenhistorie ist alles „Schaden" – dort hilft die Auswahl nicht. */}
            {bereich === "kontakt" && (
                <Auswahl titel="Kategorie" feld="kategorie" filter={filter} setze={setze} optionen={liste(werte?.kategorien)} />
            )}
            <Auswahl titel="Produktgeber" feld="produktgeber" filter={filter} setze={setze} optionen={liste(werte?.produktgeber)} />
            <Auswahl titel="Art" feld="art" filter={filter} setze={setze} optionen={liste(werte?.arten)} />
            <Auswahl titel="Priorität" feld="prioritaet" filter={filter} setze={setze} optionen={liste(werte?.prioritaeten)} />
            <FilterFeld titel="Datum von">
                <input type="date" value={filter.von ?? ""} max={filter.bis} onChange={(e) => setze("von", e.target.value)} style={feldStil} />
            </FilterFeld>
            <FilterFeld titel="Datum bis">
                <input type="date" value={filter.bis ?? ""} min={filter.von} onChange={(e) => setze("bis", e.target.value)} style={feldStil} />
            </FilterFeld>
            <Auswahl
                titel="Gelesen"
                feld="status"
                filter={filter}
                setze={setze}
                optionen={[
                    ["ungelesen", "Nur ungelesene"],
                    ["gelesen", "Nur gelesene"],
                ]}
            />
            <Auswahl
                titel="Kontakt"
                feld="zuordnung"
                filter={filter}
                setze={setze}
                optionen={[
                    ["zugeordnet", "Zugewiesen"],
                    ["vorschlag", "Nur Vorschlag"],
                    ["offen", "Ohne Kontakt"],
                ]}
            />
            <Auswahl
                titel="Vorgang"
                feld="vorgang"
                filter={filter}
                setze={setze}
                optionen={[
                    ["mit", "Mit Vorgang"],
                    ["ohne", "Ohne Vorgang"],
                ]}
            />
            <Auswahl
                titel="Dokument"
                feld="dokument"
                filter={filter}
                setze={setze}
                optionen={[
                    ["mit", "Mit Dokument"],
                    ["ohne", "Ohne Dokument"],
                ]}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: INK, alignSelf: "end", height: 30 }}>
                <input type="checkbox" checked={!!filter.kommentar} onChange={(e) => setze("kommentar", e.target.checked)} />
                Mit Kommentar
            </label>
            {dvagFilterAnzahl(filter) > 0 && (
                <button
                    type="button"
                    onClick={() => onChange({})}
                    style={{ ...linkKnopf, alignSelf: "end", height: 30, display: "inline-flex", alignItems: "center", gap: 4, justifySelf: "start" }}
                >
                    <Dismiss16Regular />
                    Filter zurücksetzen
                </button>
            )}
        </div>
    );
};

export const DvagPostView: React.FC<{ bereich: DvagBereich; ordner: DvagOrdner }> = ({ bereich, ordner }) => {
    const { isPhone, isTablet } = useResponsive();
    const schmal = isPhone || isTablet;
    const [items, setItems] = React.useState<DvagPostZeile[]>([]);
    const [gesamt, setGesamt] = React.useState(0);
    const [laedt, setLaedt] = React.useState(true);
    const [fehler, setFehler] = React.useState(false);
    const [suche, setSuche] = React.useState("");
    const [gewaehltId, setGewaehltId] = React.useState<number | null>(null);
    const [tick, setTick] = React.useState(0);
    const [seite, setSeite] = React.useState(1);
    const [seitenGroesse, setSeitenGroesse] = React.useState(50);
    const listeRef = React.useRef<HTMLDivElement | null>(null);
    const [filter, setFilter] = React.useState<DvagFilter>({});
    const [filterOffen, setFilterOffen] = React.useState(false);
    const [filterWerte, setFilterWerte] = React.useState<DvagFilterWerte | null>(null);
    const filterAnzahl = dvagFilterAnzahl(filter);
    const filterSchluessel = JSON.stringify(filter);
    const titel = DVAG_BEREICH_TITEL[bereich];

    // Filter gehören zur Historie: beim Wechsel zwischen Kontakt- und Schadenhistorie neu.
    React.useEffect(() => {
        setFilter({});
        setFilterWerte(null);
    }, [bereich]);
    React.useEffect(() => {
        // Auswahlwerte erst laden, wenn die Leiste offen ist; nach jeder Aktion frisch.
        if (!filterOffen) return;
        const ab = new AbortController();
        const lade = () =>
            dvagPostService
                .filterWerte(bereich, ab.signal)
                .then(setFilterWerte)
                .catch(() => undefined);
        lade();
        window.addEventListener(DVAG_POST_GEAENDERT, lade);
        return () => {
            ab.abort();
            window.removeEventListener(DVAG_POST_GEAENDERT, lade);
        };
    }, [bereich, filterOffen]);

    React.useEffect(() => {
        setGewaehltId(null);
        setSuche("");
    }, [ordner, bereich]);
    // Neuer Ordner, neue Suche oder neuer Filter beginnt vorn.
    React.useEffect(() => setSeite(1), [ordner, bereich, suche, filterSchluessel]);
    React.useEffect(() => {
        listeRef.current?.scrollTo({ top: 0 });
    }, [seite, ordner, bereich]);

    React.useEffect(() => {
        const ab = new AbortController();
        setLaedt(true);
        setFehler(false);
        const timer = window.setTimeout(
            () => {
                dvagPostService
                    .liste({ bereich, ordner, suche, seite, filter }, ab.signal)
                    .then((res) => {
                        setItems(res.items);
                        setGesamt(res.gesamt);
                        setSeitenGroesse(res.seitenGroesse || 50);
                        // Lag die Seite hinter dem Ende, liefert der Server die letzte.
                        if (res.seite && res.seite !== seite) setSeite(res.seite);
                        setLaedt(false);
                    })
                    .catch((err: unknown) => {
                        if ((err as { name?: string })?.name === "AbortError") return;
                        setFehler(true);
                        setLaedt(false);
                    });
            },
            suche ? 250 : 0,
        );
        return () => {
            window.clearTimeout(timer);
            ab.abort();
        };
        // `filter` steckt in `filterSchluessel` (stabil über gleiche Werte).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bereich, ordner, suche, seite, filterSchluessel, tick]);

    // Ist die Seite leergearbeitet (alles archiviert oder abgelegt), die nächsten Einträge holen.
    React.useEffect(() => {
        if (!laedt && !fehler && items.length === 0 && gesamt > 0) setTick((n) => n + 1);
    }, [laedt, fehler, items.length, gesamt]);

    const seiten = Math.max(1, Math.ceil(gesamt / seitenGroesse));
    const von = gesamt === 0 ? 0 : (seite - 1) * seitenGroesse + 1;
    const bis = Math.min(gesamt, (seite - 1) * seitenGroesse + items.length);

    const gewaehlt = items.find((i) => i.id === gewaehltId) ?? null;

    /** Nach einer Aktion: Zeile nachziehen oder, wenn sie den Ordner verlässt, entfernen. */
    const uebernehme = React.useCallback(
        (neu: DvagPostDetail) => {
            const gehoertHierher = (neu.archiviertAm != null) === (ordner === "archiv");
            setItems((alt) => (gehoertHierher ? alt.map((i) => (i.id === neu.id ? neu : i)) : alt.filter((i) => i.id !== neu.id)));
            if (!gehoertHierher) {
                setGesamt((n) => Math.max(0, n - 1));
                setGewaehltId((id) => (id === neu.id ? null : id));
            }
        },
        [ordner],
    );

    /** T-298: abgelegt — Zeile raus, die nächste wird geöffnet (wie nach „Archivieren" in Outlook). */
    const [abgelegt, setAbgelegt] = React.useState<DvagAbgelegtHinweis | null>(null);
    const entferne = React.useCallback(
        (id: number, hinweis: DvagAbgelegtHinweis) => {
            const i = items.findIndex((x) => x.id === id);
            const rest = items.filter((x) => x.id !== id);
            const naechste = i >= 0 ? (rest[i] ?? rest[i - 1] ?? null) : null;
            setItems(rest);
            if (i >= 0) setGesamt((n) => Math.max(0, n - 1));
            setGewaehltId((g) => (g === id ? (naechste?.id ?? null) : g));
            setAbgelegt(hinweis);
        },
        [items],
    );

    // T-293 (28.10.2026): Rechtsklick auf einen Eintrag — dieselben Wege wie bei
    // Mails, ohne ihn erst zu öffnen. Ohne Detail gibt es keinen Platz für eine
    // Meldung; Fehler kommen deshalb als globaler Hinweis.
    const [menue, setMenue] = React.useState<DvagPostMenue | null>(null);
    const schliesseMenue = React.useCallback(() => setMenue(null), []);
    const listenAktionen = useDvagPostAktionen({
        onGeaendert: uebernehme,
        onEntfernt: entferne,
        onFehler: (text) => {
            if (text) zeigeGlobalenHinweis(text, { art: "fehler" });
        },
    });

    const oeffne = (z: DvagPostZeile) => {
        setGewaehltId(z.id);
        if (!z.gelesen) {
            // Das Öffnen markiert serverseitig als gelesen; die Zeile zieht sofort nach.
            setItems((alt) => alt.map((i) => (i.id === z.id ? { ...i, gelesen: true } : i)));
            window.setTimeout(meldeDvagPostGeaendert, 600);
        }
    };

    const gruppen = React.useMemo(() => {
        const out: Array<{ titel: string; zeilen: DvagPostZeile[] }> = [];
        for (const z of items) {
            const titel = dvagDatumsGruppe(z.datum);
            const letzte = out[out.length - 1];
            if (letzte && letzte.titel === titel) letzte.zeilen.push(z);
            else out.push({ titel, zeilen: [z] });
        }
        return out;
    }, [items]);

    const liste = (
        <div
            ref={listeRef}
            style={{
                width: schmal ? "100%" : 360,
                minWidth: schmal ? 0 : 300,
                flexShrink: schmal ? 1 : 0,
                display: "flex",
                flexDirection: "column",
                borderRight: schmal ? "none" : `1px solid ${LINE}`,
                background: "#fff",
                overflowY: "auto",
            }}
        >
            {laedt && items.length === 0 && (
                <div style={{ padding: 24, fontSize: 13, color: INK2, textAlign: "center" }}>Einträge werden geladen …</div>
            )}
            {fehler && (
                <div role="alert" style={{ padding: 24, fontSize: 13, color: "#a4262c", textAlign: "center" }}>
                    Die {titel} konnte nicht geladen werden.{" "}
                    <button type="button" style={linkKnopf} onClick={() => setTick((n) => n + 1)}>
                        Erneut versuchen
                    </button>
                </div>
            )}
            {!laedt && !fehler && items.length === 0 && (
                <div style={{ padding: 32, fontSize: 13, color: INK2, textAlign: "center" }}>
                    {suche.trim() || filterAnzahl > 0
                        ? "Kein Eintrag passt zu Suche und Filter."
                        : ordner === "archiv"
                          ? "Im Archiv liegt noch nichts."
                          : "Der Posteingang ist leer."}
                </div>
            )}
            {gruppen.map((g) => (
                <React.Fragment key={g.titel}>
                    <div
                        style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                            background: "#fff",
                            padding: "12px 16px 4px",
                            fontSize: 12,
                            fontWeight: 600,
                            letterSpacing: 0.3,
                            color: "#7d2169",
                        }}
                    >
                        {g.titel}
                    </div>
                    {g.zeilen.map((z) => {
                        const aktiv = z.id === gewaehltId;
                        return (
                            <div
                                key={z.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => oeffne(z)}
                                onContextMenu={(e) => {
                                    e.preventDefault();
                                    setMenue({ x: e.clientX, y: e.clientY, zeile: z });
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        oeffne(z);
                                    }
                                }}
                                style={{
                                    position: "relative",
                                    padding: "10px 14px 10px 20px",
                                    cursor: "pointer",
                                    borderBottom: `1px solid ${LINE}`,
                                    borderLeft: aktiv ? `3px solid ${GOLD}` : "3px solid transparent",
                                    background: aktiv ? ACTIVE_BG : "#fff",
                                }}
                            >
                                {!z.gelesen && (
                                    <span
                                        aria-label="Ungelesen"
                                        style={{
                                            position: "absolute",
                                            left: 6,
                                            top: 16,
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            background: GOLD,
                                        }}
                                    />
                                )}
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <span
                                        style={{
                                            flex: 1,
                                            minWidth: 0,
                                            fontSize: 11.5,
                                            fontWeight: 600,
                                            color: AUB,
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {dvagArtZeile(z)}
                                    </span>
                                    {z.hatKommentar && (
                                        <span title="Kommentar in der DVAG" style={{ color: INK2, display: "inline-flex" }}>
                                            <Comment16Regular />
                                        </span>
                                    )}
                                    {z.hatPdf && (
                                        <span title="Dokument dabei" aria-label="Dokument dabei" style={{ color: INK2, display: "inline-flex" }}>
                                            <Attach16Regular />
                                        </span>
                                    )}
                                </div>
                                <div
                                    style={{
                                        fontSize: 14,
                                        fontWeight: z.gelesen ? 500 : 700,
                                        color: INK,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        marginTop: 2,
                                    }}
                                >
                                    {z.name || "Ohne Namen"}
                                </div>
                                <div
                                    style={{
                                        fontSize: 13,
                                        color: INK,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                >
                                    {z.betreff || "Kein Betreff"}
                                </div>
                                <div style={{ display: "flex", gap: 8, fontSize: 11.5, color: INK2, marginTop: 2 }}>
                                    <span style={{ flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {z.nummer ? `Nr. ${z.nummer}` : ""}
                                    </span>
                                    {z.vorgangId != null && <span style={{ color: AUB, fontWeight: 600 }}>Vorgang</span>}
                                </div>
                            </div>
                        );
                    })}
                </React.Fragment>
            ))}
            {seiten > 1 && (
                <div
                    style={{
                        position: "sticky",
                        bottom: 0,
                        marginTop: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        padding: "6px 10px",
                        borderTop: `1px solid ${LINE}`,
                        background: "#fff",
                        fontSize: 12,
                        color: INK2,
                    }}
                >
                    <button
                        type="button"
                        style={{ ...knopf(false, seite <= 1 || laedt), padding: 4 }}
                        disabled={seite <= 1 || laedt}
                        aria-label="Vorherige Seite"
                        title="Vorherige Seite"
                        onClick={() => setSeite((n) => Math.max(1, n - 1))}
                    >
                        <ChevronLeft20Regular />
                    </button>
                    <span style={{ textAlign: "center" }}>
                        {von}–{bis} von {gesamt} · Seite {seite} von {seiten}
                    </span>
                    <button
                        type="button"
                        style={{ ...knopf(false, seite >= seiten || laedt), padding: 4 }}
                        disabled={seite >= seiten || laedt}
                        aria-label="Nächste Seite"
                        title="Nächste Seite"
                        onClick={() => setSeite((n) => Math.min(seiten, n + 1))}
                    >
                        <ChevronRight20Regular />
                    </button>
                </div>
            )}
        </div>
    );

    const detail = gewaehlt ? (
        <DvagPostDetailAnsicht
            key={gewaehlt.id}
            zeile={gewaehlt}
            onGeaendert={uebernehme}
            onEntfernt={entferne}
            onSchliessen={schmal ? () => setGewaehltId(null) : undefined}
        />
    ) : (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gap: 12,
                color: INK2,
                fontSize: 13,
                padding: 24,
                textAlign: "center",
            }}
        >
            <DocumentPdf20Regular style={{ fontSize: 32 }} />
            <span>Wählen Sie einen Eintrag aus, um ihn hier zu lesen.</span>
        </div>
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", minWidth: 0, background: "#fff" }}>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "4px 12px",
                    minHeight: 40,
                    borderBottom: `1px solid ${LINE}`,
                    background: "#fff",
                    flexShrink: 0,
                }}
            >
                <MailSearchInput
                    value={suche}
                    onChange={setSuche}
                    placeholder={`${titel} durchsuchen …`}
                    busy={laedt && !!suche}
                    style={{ flex: "0 1 336px", minWidth: 0 }}
                />
                <button
                    type="button"
                    onClick={() => setFilterOffen((o) => !o)}
                    aria-expanded={filterOffen}
                    title={filterOffen ? "Filter ausblenden" : "Filter einblenden"}
                    style={{
                        ...knopf(),
                        fontWeight: 500,
                        background: filterOffen || filterAnzahl > 0 ? ACTIVE_BG : "#fff",
                        flexShrink: 0,
                    }}
                >
                    <Filter20Regular />
                    {!isPhone && "Filter"}
                    {filterAnzahl > 0 && (
                        <span
                            style={{
                                minWidth: 18,
                                height: 18,
                                borderRadius: 9,
                                background: AUB,
                                color: "#fff",
                                fontSize: 11,
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "0 5px",
                            }}
                        >
                            {filterAnzahl}
                        </span>
                    )}
                </button>
                <span style={{ fontSize: 13, color: INK2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    <strong style={{ color: INK }}>{titel}</strong> · {ORDNER_TITEL[ordner]}
                    {!laedt && !fehler ? ` · ${gesamt} ${gesamt === 1 ? "Eintrag" : "Einträge"}` : ""}
                </span>
                <button
                    type="button"
                    onClick={() => setTick((n) => n + 1)}
                    title="Liste neu laden"
                    style={{ ...knopf(), marginLeft: "auto", border: "none", fontWeight: 500 }}
                >
                    <ArrowClockwise20Regular />
                    {!isPhone && "Aktualisieren"}
                </button>
            </div>
            {filterOffen && (
                <DvagFilterLeiste bereich={bereich} filter={filter} werte={filterWerte} onChange={setFilter} />
            )}
            <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
                {schmal ? (
                    gewaehlt ? (
                        <div style={{ flex: 1, minWidth: 0 }}>{detail}</div>
                    ) : (
                        liste
                    )
                ) : (
                    <>
                        {liste}
                        <div style={{ flex: 1, flexBasis: 0, minWidth: 0, background: "#fff", overflow: "hidden" }}>{detail}</div>
                    </>
                )}
            </div>
            <DvagPostKontextMenue
                menue={menue}
                beschaeftigt={listenAktionen.beschaeftigt}
                onNeuerVorgang={() => menue && listenAktionen.neuerVorgang(menue.zeile)}
                onVorgangZuordnen={() => menue && listenAktionen.bestehendemVorgangZuordnen(menue.zeile)}
                onVorgangOeffnen={() => menue && listenAktionen.vorgangOeffnen(menue.zeile)}
                onKontaktZuweisen={() => menue && listenAktionen.kontaktZuweisen(menue.zeile)}
                onBeimKundenAblegen={() => menue && listenAktionen.beimKundenAblegen(menue.zeile)}
                onArchivUmschalten={() => menue && void listenAktionen.archivUmschalten(menue.zeile)}
                onGelesenUmschalten={() => menue && void listenAktionen.gelesenUmschalten(menue.zeile)}
                onClose={schliesseMenue}
            />
            <Toast
                open={abgelegt != null}
                tone="success"
                message={
                    abgelegt
                        ? `Abgelegt bei ${abgelegt.kontaktName}${abgelegt.ordner ? ` in „${abgelegt.ordner}“` : " in der Kundenakte"}.`
                        : ""
                }
                onClose={() => setAbgelegt(null)}
            />
        </div>
    );
};
